import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  Divider,
} from "@mui/material";
import { AuthContext } from "../context/AuthContext";
import logoImage from "../assets/logo.png";
import { useAuthentication } from 'nikao-authentication';

const LoginPage = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, loginWithToken } = useContext(AuthContext);
  const { login: msalLogin, getIdToken } = useAuthentication({ scopes: ['openid'] });
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await login(username, password);
      navigate("/dashboard");
    } catch (err) {
      // Extract error message from axios error response
      const errorData = err.response?.data || {};
      let errorMessage = "Login failed. Please try again.";
      
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleMicrosoftLogin = async () => {
    setLoading(true);
    setError("");

    try {
      await msalLogin();
      const idToken = getIdToken();

      if (!idToken) {
        setError("Could not get Microsoft sign-in token.");
        return;
      }

      const result = await loginWithToken(idToken);

      if (result.success) {
        navigate("/dashboard");
      } else {
        setError(result.message);
      }
    } catch (err) {
      const message = err.message || "Sign in with Microsoft failed. Please try again.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <Paper elevation={3} sx={{ p: 4, width: "100%" }}>
          <Box 
            display="flex" 
            flexDirection="column"
            justifyContent="center" 
            alignItems="center"
            mb={4}
            sx={{ 
              minHeight: '100px',
              width: '100%'
            }}
          >
            <img
              src={logoImage}
              alt="CR Estimator Logo"
              style={{
                height: 'auto',
                width: '100%',
                maxWidth: '300px',
                maxHeight: '120px',
                objectFit: 'contain'
              }}
            />
            <Typography 
              variant="h4" 
              component="h1" 
              sx={{ 
                mt: 2,
                fontWeight: 300,
                letterSpacing: '0.1em',
                color: '#160E2B',
                textTransform: 'uppercase'
              }}
            >
              CR-Estimator
            </Typography>
          </Box>
          <Typography variant="h6" component="h2" gutterBottom align="center" color="text.secondary" sx={{ mb: 3 }}>
            Sign In
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Username or Email"
              variant="outlined"
              margin="normal"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoComplete="username"
            />
            <TextField
              fullWidth
              label="Password"
              type="password"
              variant="outlined"
              margin="normal"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? "Signing in..." : "Sign In"}
            </Button>
            
            <Divider sx={{ my: 2 }}>OR</Divider>
            
            <Button
              type="button"
              fullWidth
              variant="outlined"
              sx={{ mt: 1, mb: 2, py: 1.5 }}
              disabled={loading}
              onClick={handleMicrosoftLogin}
            >
              Sign in with Microsoft
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default LoginPage;
